import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Download, CreditCard, Calendar, Building } from "@phosphor-icons/react";

import { PageHeader } from "@/components/layout/heading";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

interface Invoice {
  id: string;
  date: string;
  description: string;
  amount: string;
  status: "paid" | "pending" | "failed";
}

interface BillingInfo {
  companyName: string;
  billingEmail: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
}

export const Route = createFileRoute("/dashboard/settings/_layout/invoices")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <div className="min-h-full w-full @container">
      <PageHeader title="Invoices & Billing" description="View your billing history and manage payment methods" className="mb-8" />
      <InvoicesSettings />
    </div>
  );
}

function InvoicesSettings() {
  const paymentMethod = {
    type: "Visa",
    last4: "4242",
    expiryMonth: "12",
    expiryYear: "2025",
  };

  const nextBillingDate = "December 1, 2025";
  const nextBillingPlan = "Pro Plan";
  const nextBillingAmount = "$99.00";

  const invoices: Invoice[] = [
    { id: "INV-2025-001", date: "Nov 1, 2025", description: "Pro Plan - Monthly", amount: "$99.00", status: "paid" },
    { id: "INV-2025-002", date: "Oct 15, 2025", description: "Add-on Credits (500)", amount: "$100.00", status: "paid" },
    { id: "INV-2024-012", date: "Oct 1, 2024", description: "Pro Plan - Monthly", amount: "$99.00", status: "paid" },
    { id: "INV-2024-011", date: "Sep 20, 2024", description: "Add-on Credits (1000)", amount: "$200.00", status: "paid" },
    { id: "INV-2024-010", date: "Sep 1, 2024", description: "Starter Plan - Monthly", amount: "$49.00", status: "paid" },
  ];

  const billingInfo: BillingInfo = {
    companyName: "Creative Studio Inc.",
    billingEmail: "billing@creativestudio.com",
    address: {
      street: "123 Creative Street",
      city: "San Francisco",
      state: "CA",
      zip: "94102",
      country: "United States",
    },
  };

  const handleDownloadInvoice = (invoiceId: string) => {
    console.log("Downloading invoice:", invoiceId);
    toast.success("Invoice downloaded successfully");
  };

  const handleUpdatePaymentMethod = () => {
    toast.success("Payment method updated successfully");
  };

  const handleUpdateBillingInfo = () => {
    toast.success("Billing information updated successfully");
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CreditCard className="size-5 text-foreground" weight="regular" />
            <CardTitle>Payment Method</CardTitle>
          </div>
          <CardDescription>Your default payment method for subscriptions and purchases.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="size-6 text-muted-foreground" weight="regular" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  {paymentMethod.type} •••• {paymentMethod.last4}
                </p>
                <p className="text-xs text-muted-foreground">
                  Expires {paymentMethod.expiryMonth}/{paymentMethod.expiryYear}
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={handleUpdatePaymentMethod}>
              Update Payment Method
            </Button>
          </div>
        </CardContent>
      </Card>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="col-span-1">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Calendar className="size-5 text-foreground" weight="regular" />
              <CardTitle>Next Billing Date</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="text-2xl font-bold text-foreground">{nextBillingDate}</p>
              <p className="text-sm text-muted-foreground">
                {nextBillingPlan} - {nextBillingAmount} will be charged
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-1 md:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building className="size-5 text-foreground" weight="regular" />
              <CardTitle>Billing Information</CardTitle>
            </div>
            <CardDescription>Company details for invoices</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Company Name</p>
                  <p className="text-sm font-medium text-foreground">{billingInfo.companyName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Billing Email</p>
                  <p className="text-sm font-medium text-foreground">{billingInfo.billingEmail}</p>
                </div>
              </div>
              <div className="w-full">
                <p className="text-sm text-muted-foreground mb-1">Address</p>
                <p className="text-sm font-medium text-foreground">
                  {billingInfo.address.street},&nbsp;{billingInfo.address.city},&nbsp;{billingInfo.address.state}&nbsp;{billingInfo.address.zip},&nbsp;{billingInfo.address.country}
                </p>
              </div>
              <div className="pt-2 flex justify-end">
                <Button variant="outline" onClick={handleUpdateBillingInfo}>
                  Update Billing Information
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="flex flex-col gap-y-4 mt-8">
        <PageHeader title="Invoice History" description="Download and view your past invoices." />
        <div className="bg-background border border-border rounded-lg overflow-x-auto scrollbar-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="text-muted-foreground font-medium">Invoice</TableHead>
                <TableHead className="text-muted-foreground font-medium">Date</TableHead>
                <TableHead className="text-muted-foreground font-medium">Description</TableHead>
                <TableHead className="text-muted-foreground font-medium">Amount</TableHead>
                <TableHead className="text-muted-foreground font-medium">Status</TableHead>
                <TableHead className="text-muted-foreground font-medium">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id} className="border-border hover:bg-muted/50">
                  <TableCell className="font-medium text-foreground">{invoice.id}</TableCell>
                  <TableCell className="text-muted-foreground">{invoice.date}</TableCell>
                  <TableCell className="text-foreground">{invoice.description}</TableCell>
                  <TableCell className="text-foreground">{invoice.amount}</TableCell>
                  <TableCell>
                    <Badge variant={invoice.status === "paid" ? "default" : invoice.status === "pending" ? "secondary" : "destructive"} className="text-xs">
                      {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => handleDownloadInvoice(invoice.id)} className="h-auto p-0 text-foreground hover:text-foreground">
                      <Download className="size-4 mr-2" weight="regular" />
                      Download
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="text-sm text-muted-foreground">Showing {invoices.length} invoices</div>
      </section>
    </div>
  );
}
